import React from 'react';
import validator from 'validator';
import CircularProgress from '@mui/material/CircularProgress';
import {
  Box,
  Typography,
  paperClasses,
  Dialog,
  touchRippleClasses,
} from '@mui/material';
import { styled } from '@mui/system';
import IconButton from '@mui/material/IconButton';
import Input from '@mui/material/Input';
import InputAdornment from '@mui/material/InputAdornment';
import FormControl from '@mui/material/FormControl';
import Visibility from '@mui/icons-material/Visibility';
import i18n from '../../utils/i18n';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import FirebaseEventManager from '../../utils/firebaseEventManager';
import SignInBtn from './SignInBtn';

import logoImg from '../images/darkLogo.png';
import apImg from '../images/ap.png';
import gpImg from '../images/gp.png';
import gpQrcodeImg from '../images/androidQrcode.png';
import apQrcodeImg from '../images/appleQrcode.png';
import closeIcon from '../images/close.svg';

const StyledDialog = styled(Dialog)(() => ({
  [`.${paperClasses.root}`]: {
    maxWidth: 716,
    borderRadius: 16,
    padding: 32,
    '@media (max-width: 992px)': {
      padding: '20px 20px 40px',
    },
  },
}));

const DialogContent = styled(Box)(() => ({
  display: 'flex',
  padding: '40px 0 20px 0',
  '> div:last-child': {
    borderLeft: '2px solid #EAEAEA',
  },
  button: {
    display: 'none',
  },
  '@media (max-width: 992px)': {
    button: {
      display: 'block',
    },
    padding: '24px 0 0 0',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 16,
    img: {
      width: 162,
      height: 48,
    },
  },
}));

const QRCodeBox = styled(Box)(() => ({
  minWidth: 326,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  margin: '24px 0',
  '> div': {
    marginBottom: 16,
    color: '#676767',
    fontWeight: 700,
    fontSize: 20,
  },
  '> img': {
    width: 200,
    height: 200,
  },
  '@media (max-width: 992px)': {
    display: 'none',
  },
}));

const Root = styled('div')({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'column',
  overflow: 'auto',
  background: '#F8F8F8',
  padding: '32px 0',
});
const Container = styled('div')({
  margin: 'auto',
});
const ImageContainer = styled('p')({
  textAlign: 'center',
  fontSize: 0,
});
const SignInIntro = styled('p')({
  fontFamily: 'Open Sans',
  fontStyle: 'normal',
  fontWeight: 600,
  fontSize: '17px',
  lineHeight: '23px',
  textAlign: 'center',
  color: '#757575',
  margin: '16px auto',
  maxWidth: 375,
});
const Form = styled('form')({
  padding: 24,
  paddingBottom: 16,
  background: '#FFFFFF',
  border: '1px solid #E1E1E1',
  boxSizing: 'border-box',
  borderRadius: 4,
  maxWidth: 375,
  margin: 'auto',
});
const FormLabel = styled('p')({
  fontStyle: 'normal',
  fontWeight: 700,
  lineHeight: '19px',
  color: '#181818',
  textAlign: 'left',
  marginBottom: 8,
  marginTop: 0,
});
const FormInput = styled(Input)({
  caretColor: '#EF8D0E',
  '&.Mui-error': {
    '&:before, &:after': {
      borderBottom: '1px solid #E9683F !important',
    },
  },
  '&:not(.Mui-error)': {
    '&:before, &:after': {
      borderBottom: '1px solid #EAEAEA !important',
    },
  },
  '&.focused': {
    '&:not(.Mui-error)': {
      '&:before, &:after': {
        borderBottom: '1px solid #181818 !important',
      },
    },
  }
});
const FormErrorText = styled('p')({
  fontSize: 14,
  lineHeight: '16px',
  height: '16px',
  color: '#E9683F',
  textAlign: 'left',
  visibility: 'hidden',
  marginTop: 4,
  marginBottom: 4,

  '&.visible': {
    fontSize: 14,
    lineHeight: '16px',
    color: '#E9683F',
    textAlign: 'left',
    visibility: 'visible',
    marginTop: 4,
    marginBottom: 24,
  }
});
const EmailSignInBtn = styled('button')({
  width: '100%',
  height: 48,
  background: '#FCC35D',
  borderRadius: 4,
  fontWeight: 600,
  fontSize: 17,
  lineHeight: '23px',
  textAlign: 'center',
  color: '#181818',
  border: 'initial',
  cursor: 'pointer',
  marginTop: 8,
  '&:hover': {
    filter: 'brightness(105%)',
  },
  '&.disabled': {
    width: '100%',
    height: 48,
    fontFamily: 'Roboto, sans-serif',
    background: '#C7C7C7',
    borderRadius: 8,
    fontWeight: 500,
    fontSize: 16,
    lineHeight: '19px',
    textAlign: 'center',
    color: '#FFFFFF',
    border: 'initial',
    cursor: 'not-allowed',
    marginTop: 8,
  }
});
const SignInBtnContainer = styled('div')({
  marginBottom: 16,
});
const SeparateLine = styled('p')({
  position: 'relative',
  height: '1px',
  width: '100%',
  background: '#999',
  margin: '35px 0',
  textAlign: 'center',
  '&::after': {
    content: '"or"',
    position: 'absolute',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 31,
    height: '24px',
    backgroundColor: '#FFFFFF',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    color: '#999',
    fontSize: 16,
    fontWeight: 'normal',
  },
});
const ApImg = styled('img')({
  cursor: 'pointer',
});
const GpImg = styled('img')({
  cursor: 'pointer',
});
const SignUpText = styled('div')({
  color: '#676767',
  margin: '12px 0 24px 0',
  lineHeight: '24px',
  '& > a': {
    color: '#EF8D0E',
    textDecoration: 'underline',
    cursor: 'pointer',
  },
});
const CloseIconBtn = styled('button')({
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: 0,
  width: 32,
  height: 32,
});
const CleanButton = styled('button')({
  border: 'none',
  background: 'none',
  cursor: 'pointer',
});

export default function EmailSignIn(props: {
  signInWithGoogle: () => void;
  signInWithEmail: (data: {
    email?: string;
    password?: string;
    error?: Error | null;
  }) => Promise<boolean>;
  signInWithApple: () => void;
  fetchSignInMethodsForEmail: (email: string) => Promise<string[]>;
  downloadApp: (data: { platform: string; page: string }) => void;
  hideLogoImg?: boolean;
  hideSignInIntro?: boolean;
}) {
  const {
    signInWithGoogle,
    signInWithEmail,
    signInWithApple,
    fetchSignInMethodsForEmail,
    hideLogoImg,
    hideSignInIntro,
    downloadApp,
  } = props;
  const firebaseEventManager = new FirebaseEventManager();
  const [isShowPassword, setIsShowPassword] = React.useState(false);
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [emailError, setEmailError] = React.useState<React.ReactNode | null>(
    null
  );
  const [passwordError, setPasswordError] =
    React.useState<React.ReactNode | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [openSignUpDialog, setOpenSignUpDialog] = React.useState(false);

  React.useEffect(() => {
    firebaseEventManager.signInEmailPageLanded();
  }, []);

  const emailInputEl = React.useRef(null);

  async function changeEmail(event: React.ChangeEvent<HTMLInputElement>) {
    setEmail(event.target.value);
    if (!checkEmailFormat()) {
      setEmailError(i18n.t('email_signin_page_email_invalid'));
      return false;
    } else {
      setEmailError(null);
      return true;
    }
  }
  function changePassword(event: React.ChangeEvent<HTMLInputElement>) {
    setPassword(event.target.value);
    setPasswordError(null);
  }

  async function checkUserEmail() {
    if (!checkEmailFormat()) {
      setEmailError(i18n.t('email_signin_page_email_invalid'));
      return false;
    }
    const result = await fetchSignInMethodsForEmail(email);
    if (result.length === 0) {
      setEmailError(i18n.t('email_signin_page_email_unregistered'));
      return false;
    }
    setEmailError(null);
    return true;
  }

  function checkEmailFormat() {
    return validator.isEmail(email);
  }

  async function submit(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    if (isSubmitting) {
      return;
    }
    if (emailError) {
      return;
    }
    if (email === '' || password === '') {
      return;
    }
    try {
      setIsSubmitting(true);

      firebaseEventManager.signInEmailPageClick({
        item: 'email',
      });

      const emailValidateResult = await checkUserEmail();
      if (!emailValidateResult) {
        return;
      }

      const canTry = canTryPassword(email);
      if (!canTry) {
        return await signInWithEmail({
          error: new Error('ExceedTryMax'),
        });
      }
      const result = await signInWithEmail({
        email,
        password,
      });
      if (result === true) {
        resetTryTime(email);
      }
    } catch (error) {
      const isGmailRegex = /(gmail\.com$)|(googlemail\.com$)/i;
      const errorCode = (error as { code?: string }).code;
      if (
        isGmailRegex.test(email) &&
        (errorCode === 'auth/wrong-password' ||
          errorCode === 'auth/user-not-found')
      ) {
        tryPasswordFailed(email);
        firebaseEventManager.sendLoginFailed({
          provider: 'email',
          type: 'manual',
          code: errorCode || null,
          customError: 'please_try_google',
        });
        return setPasswordError(i18n.t('email_signin_page_use_google_error'));
      }

      const isAppleRegex = /@privaterelay\.appleid\.com$/i;
      if (
        isAppleRegex.test(email) &&
        (errorCode === 'auth/wrong-password' ||
          errorCode === 'auth/user-not-found')
      ) {
        tryPasswordFailed(email);
        firebaseEventManager.sendLoginFailed({
          provider: 'email',
          type: 'manual',
          code: errorCode || null,
          customError: 'please_try_apple',
        });
        return setPasswordError(i18n.t('email_signin_page_use_apple_error'));
      }

      if (errorCode === 'auth/wrong-password') {
        tryPasswordFailed(email);
        firebaseEventManager.sendLoginFailed({
          provider: 'email',
          type: 'manual',
          code: errorCode || null,
          customError: 'wrong_password',
        });
        return setPasswordError(i18n.t('email_signin_page_wrong_email_pwd'));
      }

      if (errorCode === 'auth/user-not-found') {
        // to distinguish apple private sign in
        const signInMethods = await fetchSignInMethodsForEmail(email);
        if (signInMethods.length > 0) {
          tryPasswordFailed(email);
          firebaseEventManager.sendLoginFailed({
            provider: 'email',
            type: 'manual',
            code: errorCode || null,
            customError: 'wrong_password',
          });
          return setPasswordError(i18n.t('email_signin_page_wrong_email_pwd'));
        }
        return setEmailError(i18n.t('email_signin_page_email_unregistered'));
      }

      return signInWithEmail({
        error: error as Error,
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleOpenSignUpDialog(e: React.MouseEvent<HTMLDivElement>) {
    const isLinkTag = (e.target as HTMLElement).tagName === 'A';
    if (!isLinkTag) return;
    setOpenSignUpDialog(true);
  }

  function closeSignUpDialog() {
    setOpenSignUpDialog(false);
  }

  const isSubmitBtnValid = email.length > 0 && password.length > 0;
  const email_signin_page_signup_tip = i18n.t('email_signin_page_signup_tip');
  const sign_in_same_account = i18n.t('sign_in_same_account');
  const matchPadMediaWidth = window.matchMedia('(max-width: 992px)');
  const hasError = emailError || passwordError;
  return <Root>
    <Container>
      {!hideLogoImg && <ImageContainer>
        <img src={logoImg} width='240' />
      </ImageContainer>}
      {!hideSignInIntro && <SignInIntro dangerouslySetInnerHTML={{ __html: sign_in_same_account }} />}
      <Form>
        <FormControl variant='standard' sx={{ width: '100%' }}>
          <FormLabel>{i18n.t('email_signin_page_email')}</FormLabel>
          <FormInput
            type='text'
            value={email}
            inputRef={emailInputEl}
            onChange={changeEmail}
            onBlur={checkUserEmail}
            className={
              emailError
                ? 'Mui-error'
                : undefined
            }
            classes={{
              focused: 'focused',
            }}
            placeholder={i18n.t('email_signin_page_email_placeholder')}
          />
          <FormErrorText className={emailError ? 'visible' : undefined}>
            {emailError}
          </FormErrorText>
        </FormControl>
        <FormControl variant='standard' sx={{ width: '100%' }}>
          <FormLabel>{i18n.t('email_signin_page_password')}</FormLabel>
            <FormInput
              id='standard-adornment-password'
              type={isShowPassword ? 'text' : 'password'}
              className={passwordError ? 'Mui-error' : undefined}
              classes={{ focused: 'focused' }}
              value={password}
              onChange={changePassword}
              endAdornment={
                <InputAdornment position='end'>
                  <IconButton
                    aria-label='toggle password visibility'
                    onClick={() => setIsShowPassword(!isShowPassword)}
                    sx={{
                      padding: '0 !important',
                      marginLeft: '10px !important',
                      '&:hover': {
                        backgroundColor: 'transparent !important',
                      },
                      [`& > .${touchRippleClasses.root}`]: {
                        display: 'none !important',
                      },
                    }}
                    size='large'>
                    {isShowPassword ? (
                      <VisibilityOffIcon sx={{ '& > *': { fill: '#999999' }}} />
                    ) : (
                      <Visibility
                        fontSize='medium'
                        sx={{ '& > *': { fill: '#999999' }}}
                      />
                    )}
                  </IconButton>
                </InputAdornment>
              }
              placeholder={i18n.t('email_signin_page_password_placeholder')}
            />
            <FormErrorText className={passwordError ? 'visible' : undefined}>
              {passwordError}
            </FormErrorText>
        </FormControl>
          <EmailSignInBtn
            // disabled={!isSubmitBtnValid && !isSubmitting}
            className={
              isSubmitBtnValid && !hasError
                ? undefined
                : 'disabled'
            }
            onClick={submit}>
            {isSubmitting ? (
              <CircularProgress size={20} color='inherit' id='signInLoading' />
            ) : (
              i18n.t('email_signin_page_email_signin_btn')
            )}
          </EmailSignInBtn>
          <SignUpText
            dangerouslySetInnerHTML={{ __html: email_signin_page_signup_tip }}
            onClick={handleOpenSignUpDialog}
          />
          <SeparateLine />
          <SignInBtnContainer
            onClick={(event) => signInWithGoogle()}>
            <SignInBtn type='google' />
          </SignInBtnContainer>
          <SignInBtnContainer
            onClick={(event) => signInWithApple()}>
            <SignInBtn type='apple' />
          </SignInBtnContainer>
        </Form>
      </Container>
      <StyledDialog open={openSignUpDialog} onClose={closeSignUpDialog}>
        <Box
          display='flex'
          alignItems='center'
          justifyContent='space-between'
          mb='20px'>
          <Typography
            component='h4'
            fontWeight={700}
            fontSize={{ xs: 20, lg: 24 }}
            lineHeight={{ xs: '23px', lg: '28px' }}>
            {matchPadMediaWidth.matches
              ? i18n.t('sign_up_title_mobile')
              : i18n.t('sign_up_title')}
          </Typography>
          <CloseIconBtn onClick={closeSignUpDialog}>
            <img src={closeIcon} alt='close' />
          </CloseIconBtn>
        </Box>
        <DialogContent>
          <QRCodeBox>
            <div>Android</div>
            <img src={gpQrcodeImg} alt='google play' />
          </QRCodeBox>
          <QRCodeBox>
            <div>iOS</div>
            <img src={apQrcodeImg} alt='app store' />
          </QRCodeBox>
          <CleanButton
            onClick={() =>
              downloadApp({ platform: 'android', page: 'emailsignin' })
            }>
            <GpImg src={gpImg} alt='google play' />
          </CleanButton>
          <CleanButton
            onClick={() =>
              downloadApp({ platform: 'ios', page: 'emailsignin' })
            }>
            <ApImg src={apImg} alt='app store' />
          </CleanButton>
        </DialogContent>
      </StyledDialog>
  </Root>;
}

const storageKey = 'Alfred::trypasswrod';
function resetTryTime(email: string) {
  const raw = localStorage.getItem(storageKey);
  if (raw) {
    const data = JSON.parse(raw);
    data[email] = [];
    localStorage.setItem(storageKey, JSON.stringify(data));
  }
}

function canTryPassword(email: string) {
  const raw = localStorage.getItem(storageKey);
  const now = new Date().getTime();
  try {
    if (raw) {
      const data: Record<string, number[]> = JSON.parse(raw);
      if (data[email]) {
        // this should be timestamp list
        const twentyFour = 24 * 60 * 60 * 1000;
        const in24HourLockList = data[email].filter(
          (t) => now - t < twentyFour
        );
        if (in24HourLockList.length >= 10) {
          return false;
        }
      }
    }
    return true;
  } catch (error) {}
}

function tryPasswordFailed(email: string) {
  const raw = localStorage.getItem(storageKey);
  const now = new Date().getTime();
  let data: Record<string, number[]> | undefined;
  try {
    if (raw) {
      data = JSON.parse(raw) as Record<string, number[]>;
      if (data[email]) {
        // this should be timestamp list
        const twentyFour = 24 * 60 * 60 * 1000;
        const in24HourLockList = data[email].filter(
          (t) => now - t < twentyFour
        );
        if (in24HourLockList.length >= 10) {
          return false;
        } else {
          in24HourLockList.push(now);
          data[email] = in24HourLockList;
          return true;
        }
      } else {
        data[email] = [now];
        return true;
      }
    } else {
      data = {
        [email]: [now],
      };
      return true;
    }
  } finally {
    if (data) localStorage.setItem(storageKey, JSON.stringify(data));
  }
}
